'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useSession, signOut } from 'next-auth/react';
import { toast } from '@/lib/toast';
import {
  ACCOUNT_NOT_PROVISIONED_ERROR,
  forceFederatedLogout
} from '@/lib/auth/federated-logout';

/**
 * Paths that authorise themselves (token-in-URL, public portal etc.) and
 * must never be force-redirected to /login by an out-of-band NextAuth
 * refresh failure. Kept in sync with `proxy.ts` `publicPaths` and
 * `providers-wrapper.tsx` `PUBLIC_PATHS`.
 */
const AUTH_REDIRECT_EXEMPT_PREFIXES = [
  '/device-recording', // LF-3743 mobile handoff
  '/portal', // Beneficiary portal
  '/accept-invitation',
  '/auth-error',
  '/callback',
  '/login'
];

function isExemptPath(pathname: string | null): boolean {
  if (!pathname) return false;
  return AUTH_REDIRECT_EXEMPT_PREFIXES.some((p) => pathname.startsWith(p));
}

/**
 * Hook to handle authentication errors automatically
 *
 * When token refresh fails (session.error === 'RefreshAccessTokenError'),
 * this hook automatically signs out the user and redirects to login.
 *
 * This follows NextAuth best practices for handling refresh token expiration.
 *
 * @see https://next-auth.js.org/tutorials/refresh-token-rotation
 *
 * @example
 * ```tsx
 * export function MyComponent() {
 *   useAuthErrorHandler(); // Add this to handle auth errors
 *
 *   const { data: session } = useSession();
 *   // ... rest of component
 * }
 * ```
 */
export function useAuthErrorHandler() {
  const { data: session, status } = useSession();
  const pathname = usePathname();

  useEffect(() => {
    // Only check when authenticated
    if (status !== 'authenticated') return;

    // LF-3743: public surfaces (mobile handoff, portal, login flows) must
    // never be ripped out from under the user by an out-of-band NextAuth
    // refresh failure. The on-page UI is responsible for surfacing token
    // expiry; a hard redirect to /login was breaking the mobile capture
    // flow when a stale lawyer session existed on the same device.
    if (isExemptPath(pathname)) return;

    // Check if there's a refresh token error
    if (session?.error === 'RefreshAccessTokenError') {
      console.error(
        '[useAuthErrorHandler] Session has RefreshAccessTokenError, signing out'
      );

      // Show user-friendly message
      toast.error('Your session has expired. Please sign in again.');

      // Sign out and redirect to login
      // Use callbackUrl to redirect back after sign in
      signOut({
        callbackUrl: window.location.pathname + window.location.search,
        redirect: true
      });
      return;
    }

    // LF-4870: a validly-authenticated session with NO tenant_id is
    // unrecoverable — the backend rejects every tenant-scoped call, and a
    // plain signIn would silently re-mint the same tenant-less session from
    // Keycloak's SSO cookie (the infinite login loop). Force a full federated
    // logout (ends the Keycloak session too) so the user lands on a clean
    // login page. Distinct from RefreshAccessTokenError above (handled first),
    // which is a recoverable re-auth, not a broken session.
    // LF-5950: this is the client-side detection of the same shape the backend
    // reports as ACCOUNT_NOT_PROVISIONED, so it must land on the same "Account
    // not set up" card — otherwise the user sees a different explanation
    // depending on which check happened to fire first.
    if (!session?.tenantId) {
      console.error(
        '[useAuthErrorHandler] Authenticated session has no tenant_id — forcing federated logout'
      );
      toast.error("Your account isn't set up for this firm.");
      forceFederatedLogout(session?.idToken, ACCOUNT_NOT_PROVISIONED_ERROR);
    }
  }, [session, status, pathname]);
}
